import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity, Share, ImageBackground, Alert, Linking, Animated } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, router } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Svg, Path } from 'react-native-svg';
import { LinearGradient } from 'expo-linear-gradient';
import { useAuth } from '@/contexts/AuthContext';
import { useNotes } from '@/contexts/NotesContext';
import Avatar from '@/components/Avatar';
import Accordion from '@/components/Accordion';
import ClipboardService from '@/services/clipboardService';
import { CheckinService } from '@/services/checkinService';
import { SavedMeetingsService } from '@/services/savedMeetingsService';
import { MeetingsService } from '@/services/meetingsService';
import meetingStatsService from '@/services/meetingStatsService';
import { getMeetingTimeStatus, getMeetingDisplayTime, formatDisplayTime, isMeetingLive } from '@/utils/meetingUtils';
import { Meeting } from '@/types/meetings';
import { Skeleton, SkeletonCircle } from '@/components/Skeleton';
import { useLanguage } from '@/contexts/LanguageContext';
import { normalizeWeekdayValue, parseWeekdays, translateCategory, translatePlatform, translateWeekdays } from '@/utils/meetingLocale';
import { translateMeeting, translateGroup } from '@/utils/meetingTranslation';
import { MeetingGroup } from '@/types/groups';
import { usePadrinhoPlus } from '@/hooks/useEntitlement';
import RevenueCatService from '@/services/revenueCatService';

export default function MeetingDetailsPage() {
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t, language } = useLanguage();
  const { user, userProfile } = useAuth();
  const { addNote } = useNotes();
  const { hasPadrinhoPlus } = usePadrinhoPlus();
  
  // State
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [isCheckinLoading, setIsCheckinLoading] = useState(false);
  const [participantCount, setParticipantCount] = useState(0);
  const [tipsExpanded, setTipsExpanded] = useState(false);
  const [programacaoExpanded, setProgramacaoExpanded] = useState(false);
  const [sessions, setSessions] = useState<Array<{ id: string; weekday: string; meeting_time: string; meeting_category: string | null }>>([]);
  const [recentAttendees, setRecentAttendees] = useState<{ userId: string; profilePhotoUrl: string | null }[]>([]);
  const [groupData, setGroupData] = useState<MeetingGroup | null>(null);
  const [groupName, setGroupName] = useState<string | null>(null);
  const [groupSlug, setGroupSlug] = useState<string | null>(null);

  // Load meeting data
  useEffect(() => {
    if (id) {
      loadMeetingData();
      // PRD §5.6 — `meeting_detail_viewed`. Fire on every mount; Clarity
      // dedupes per session. The detail screen is shared by all meeting
      // types — Supabase-stored AA/NA meetings are the dominant case
      // today, so `meeting_source` defaults to `'supabase'` and
      // `meeting_type` to `'video'`. Refine when Elo-sourced meetings
      // ship a distinct route.
      import('@/utils/clarityEvents')
        .then(({ logMeetingDetailViewed }) =>
          logMeetingDetailViewed({
            meeting_source: 'supabase',
            meeting_type: 'video',
            meeting_id: String(id),
          })
        )
        .catch(() => {});
    }
  }, [id, language]);

  // Re-translate group when language changes
  useEffect(() => {
    const translateGroupName = async () => {
      if (groupData) {
        const languageMap: Record<string, string> = { 'pt': 'pt-BR', 'en': 'en-US', 'es': 'es-ES' };
        const targetLanguage = languageMap[language] || 'pt-BR';
        const translatedGroup = await translateGroup(groupData, targetLanguage);
        const community = (groupData as any).community;
        setGroupName((community && community.trim()) || translatedGroup.name);
      }
    };
    translateGroupName();
  }, [groupData, language]);

  const loadMeetingData = async () => {
    try {
      setLoading(true);
      console.log('📅 Loading meeting data via API:', id);
      
      // Fetch meeting details with group via API (bypasses Supabase client timing issues)
      const { meeting: meetingData, group: fetchedGroupData, sessions: fetchedSessions } = await MeetingsService.getMeetingWithGroup(id);

      if (!meetingData) {
        throw new Error('Meeting not found');
      }
      
      // Translate meeting content based on user language
      const languageMap: Record<string, string> = { 'pt': 'pt-BR', 'en': 'en-US', 'es': 'es-ES' };
      const targetLanguage = languageMap[language] || 'pt-BR';
      const translatedMeeting = await translateMeeting(meetingData, targetLanguage);
      
      setMeeting(translatedMeeting);
      setSessions(fetchedSessions || []);

      // Set group data if it exists (already fetched with meeting)
      if (fetchedGroupData) {
        setGroupData(fetchedGroupData);
        // Elo groups don't carry a slug — fall back to the prefixed group id
        // (e.g. "elo:1"), which /api/meetings/groups?slug= already accepts.
        setGroupSlug(fetchedGroupData.slug || fetchedGroupData.id || null);

        // Translate group name based on user language. Prefer the short
        // community label (e.g. "AA") over the long meeting/group name when
        // the Elo group exposes one — mirrors the chip on encontros-online.
        const translatedGroup = await translateGroup(fetchedGroupData, targetLanguage);
        const community = (fetchedGroupData as any).community;
        setGroupName((community && community.trim()) || translatedGroup.name);
      }

      // Check if user has saved this meeting
      if (user) {
        const saved = await SavedMeetingsService.isMeetingSaved(user.id, id);
        setIsSaved(saved);
      }

      // Get global meeting stats (monthly count + recent attendees) using server API
      // This bypasses RLS to show data from ALL users, not just current user
      try {
        const stats = await meetingStatsService.getMeetingStats(id);
        if (stats) {
          setParticipantCount(stats.totalParticipantCount);
          setRecentAttendees(stats.recentAttendees);
          console.log('✅ Loaded global meeting stats:', stats.totalParticipantCount, 'participants');
        }
      } catch (error) {
        console.error('Error loading global meeting stats:', error);
        setParticipantCount(0);
        setRecentAttendees([]);
      }
    } catch (error) {
      console.error('Error loading meeting:', error);
      Alert.alert('Erro', 'Não foi possível carregar as informações do encontro');
    } finally {
      setLoading(false);
    }
  };

  // Whether this meeting requires Padrinho Plus to enter
  const requiresPlus = !!(
    meeting?.plus_required ||
    meeting?.access_tier === 'plus' ||
    groupData?.plus_required ||
    groupData?.is_plus ||
    groupData?.access_tier === 'plus'
  );
  const showPlusGate = requiresPlus && !hasPadrinhoPlus;

  // Handle check-in and join meeting
  const handleEntrar = async () => {
    if (!meeting) return;
    
    if (!user) {
      Alert.alert('Erro', 'É necessário fazer login para entrar no encontro');
      return;
    }

    // Plus gate: open RevenueCat paywall instead of joining the meeting
    if (showPlusGate) {
      try {
        await RevenueCatService.presentPaywall({ fromSurface: 'meeting_detail' });
      } catch (err) {
        console.error('Error presenting paywall:', err);
      }
      return;
    }
    
    setIsCheckinLoading(true);

    // PRD §5.6 — `meeting_join_tapped`. Fired before the network call so
    // intent volume is captured even when the check-in fails. The success
    // outcome is captured by `meeting_checkin_submitted` inside the
    // service. Source/type defaulted as in the detail-view event above.
    import('@/utils/clarityEvents')
      .then(({ logMeetingJoinTapped }) =>
        logMeetingJoinTapped({
          meeting_source: 'supabase',
          meeting_type: 'video',
          meeting_id: meeting.id,
        })
      )
      .catch(() => {});

    try {
      // Perform check-in
      const deviceInfo = CheckinService.getDeviceInfo();
      const today = new Date().toISOString();
      
      await CheckinService.checkIn({
        meetingId: meeting.id,
        meetingDate: today,
        deviceInfo,
        notes: 'Check-in via mobile app',
        onNoteCreate: addNote
      });

      // Open meeting link
      if (meeting.meeting_link) {
        let cleanUrl = meeting.meeting_link.trim();
        if (cleanUrl.startsWith('file://')) {
          cleanUrl = cleanUrl.split('/').pop() || '';
        }
        let finalUrl = cleanUrl;
        if (!finalUrl.startsWith('http://') && !finalUrl.startsWith('https://')) {
          finalUrl = `https://${finalUrl}`;
        }
        
        await Linking.openURL(finalUrl);

        // PRD §4 — `meeting_joined` macro event. Fires only when the external
        // meeting URL successfully opened. This screen handles the AA/NA-style
        // external meeting flow, so meeting_source and meeting_type are both
        // 'external'.
        import('@/utils/clarityEvents')
          .then(({ logMeetingJoined }) =>
            logMeetingJoined({
              meeting_source: 'external',
              meeting_type: 'external',
              meeting_id: String(id),
            })
          )
          .catch(() => {});
      } else {
        Alert.alert('Aviso', 'Link do encontro não disponível');
      }
    } catch (error) {
      console.error('Error during check-in:', error);
    } finally {
      setIsCheckinLoading(false);
    }
  };

  // Handle favorite toggle
  const handleFavoritar = async () => {
    if (!user || !meeting) return;

    // Optimistic update - change UI immediately
    const newIsSaved = !isSaved;
    setIsSaved(newIsSaved);

    // Perform the save operation in the background
    try {
      const result = await SavedMeetingsService.toggleSavedMeeting(user.id, meeting.id);
      
      // If the operation failed, revert the optimistic update
      if (!result.success) {
        setIsSaved(!newIsSaved);
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      // Revert optimistic update on error
      setIsSaved(!newIsSaved);
    }
  };

  // Handle share
  const handleConvidar = async () => {
    if (!meeting) return;

    import('@/utils/clarityEvents')
      .then(({ logUiAction }) =>
        logUiAction({ surface: 'meeting_detail', element: 'invite' })
      )
      .catch(() => {});

    try {
      const formattedWeekdays = translateWeekdays(meeting.meeting_weekday, t, false);
      
      let message = `Olá! Gostaria de te convidar para participar de um encontro comigo. 🙏\n\n`;
      message += `📅 *${meeting.meeting_name}*\n\n`;
      message += `🕐 *${t('meetings.labels.schedule')}:* ${formattedWeekdays} às ${formatDisplayTime(meeting.meeting_time)}\n`;

      // Plus-only meetings must NOT leak the meeting link / password — that
      // would bypass the paywall. Everyone gets the app download CTA at the end.
      if (!requiresPlus) {
        if (meeting.meeting_link) {
          message += `🔗 *Link:* ${meeting.meeting_link}\n`;
        }
        if (meeting.meeting_password) {
          message += `🔑 *${t('meetings.labels.password')}:* ${meeting.meeting_password}\n`;
        }
      }

      message += `\nBaixe o app do Padrinho em www.padrinho.app`;
      
      await Share.share({
        message,
        title: 'Convite para encontro',
      });
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  // Copy password to clipboard
  const handleCopyPassword = async () => {
    if (!meeting?.meeting_password) return;

    import('@/utils/clarityEvents')
      .then(({ logUiAction }) =>
        logUiAction({ surface: 'meeting_detail', element: 'copy_password' })
      )
      .catch(() => {});

    await ClipboardService.copyWithFeedback(
      meeting.meeting_password,
      t('meetings.clipboard.passwordCopied'),
      t('meetings.labels.password')
    );
  };

  if (loading || !meeting) {
    return (
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
          {/* Header Skeleton */}
          <View style={styles.headerContainer}>
            <Skeleton width="100%" height={300} borderRadius={0} />
          </View>

          {/* Avatar Group Skeleton */}
          <View style={styles.avatarGroupContainer}>
            <View style={{ flexDirection: 'row' }}>
              {[0, 1, 2].map((i) => (
                <View key={i} style={{ marginLeft: i > 0 ? -8 : 0 }}>
                  <SkeletonCircle size={36} />
                </View>
              ))}
            </View>
            <Skeleton width={120} height={12} />
          </View>

          {/* Content Skeleton */}
          <View style={{ paddingHorizontal: 24, gap: 24, paddingTop: 24 }}>
            {/* Title */}
            <Skeleton width="80%" height={28} />
            
            {/* Info chips */}
            <View style={{ flexDirection: 'row', gap: 8 }}>
              <Skeleton width={100} height={32} borderRadius={17} />
              <Skeleton width={120} height={32} borderRadius={17} />
            </View>

            {/* Description */}
            <View style={{ gap: 8 }}>
              <Skeleton width="100%" height={16} />
              <Skeleton width="95%" height={16} />
              <Skeleton width="85%" height={16} />
            </View>

            {/* Buttons */}
            <View style={{ gap: 12 }}>
              <Skeleton width="100%" height={56} borderRadius={12} />
              <Skeleton width="100%" height={56} borderRadius={12} />
            </View>
          </View>
        </ScrollView>
      </SafeAreaView>
    );
  }

  const timeStatus = getMeetingTimeStatus(meeting.meeting_time, meeting.meeting_weekday, t);
  const formattedWeekdays = translateWeekdays(meeting.meeting_weekday, t, false);
  const { displayText: nextMeetingText, isLive: isNextMeetingLive } = getMeetingDisplayTime(meeting.meeting_time, meeting.meeting_weekday, t);

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView style={styles.scrollView} showsVerticalScrollIndicator={false}>
        {/* Header with Image Background */}
        <View style={styles.headerContainer}>
          <ImageBackground
            source={
              meeting.header_image_url 
                ? { uri: meeting.header_image_url } 
                : require('../../assets/images/image_1763325486152.png')
            }
            style={styles.headerImage}
            imageStyle={styles.headerImageStyle}
          >
            <LinearGradient
              colors={['rgba(249, 248, 243, 0)', '#F9F8F3']}
              locations={[0.45, 0.7]}
              style={styles.gradient}
            >
              <View style={styles.headerContent}>
                {requiresPlus && hasPadrinhoPlus && (
                  <View style={styles.plusBadge}>
                    {/* Same gem icon used across encounters / group cards /
                        the action button below — replaces the legacy star
                        so the badge is visually consistent app-wide. */}
                    <Svg width="16" height="16" viewBox="0 0 12 12" fill="none">
                      <Path d="M1.75524 5.7637L5.40815 9.7771C5.72535 10.1259 6.2741 10.1259 6.59125 9.7771L10.2447 5.7632C10.5502 5.42805 10.5852 4.92698 10.3298 4.55191L8.8587 2.39099C8.6763 2.12343 8.37275 1.96289 8.04875 1.96289H3.95555C3.63156 1.96289 3.32849 2.12294 3.14606 2.3905L1.67059 4.55191C1.4147 4.92649 1.44973 5.42805 1.75524 5.7637Z" stroke="#FFFFFF" strokeLinecap="round" strokeLinejoin="round" />
                      <Path d="M6.99847 1.96289L7.68197 4.55191C7.78117 4.92698 7.76757 5.42805 7.64887 5.7632L6.22887 9.7771C6.10577 10.1259 5.89222 10.1259 5.76912 9.7771L4.34912 5.7637C4.23042 5.42805 4.2168 4.92649 4.31653 4.55191L5.00197 1.96289" stroke="#FFFFFF" strokeLinecap="round" strokeLinejoin="round" />
                      <Path d="M1.50391 5.10156H10.4954" stroke="#FFFFFF" strokeLinecap="round" strokeLinejoin="round" />
                    </Svg>
                    <Text style={styles.plusBadgeText}>Plus</Text>
                  </View>
                )}
                <Text style={styles.meetingName}>{meeting.meeting_name}</Text>
                
                <View style={styles.statusRow}>
                  <View style={styles.statusChip}>
                    <Svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                      <Path d="M12.627 3.07693V5.55637" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M6.60938 3.07693V5.55637" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path fillRule="evenodd" clipRule="evenodd" d="M13.0698 4.22025L6.17451 4.22093C4.14908 4.22093 2.88477 5.34882 2.88477 7.42196V13.663C2.88477 15.7689 4.14908 16.9229 6.17451 16.9229L13.0639 16.9222C15.0953 16.9222 16.3536 15.7884 16.3536 13.7145V7.42128C16.3596 5.34814 15.1012 4.22025 13.0698 4.22025Z" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M6.27148 9.53122H6.27982" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M6.27148 12.4099H6.27982" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M9.61523 9.53122H9.62357" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M9.61523 12.4099H9.62357" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M12.9512 9.53122H12.9595" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <Path d="M12.9512 12.4099H12.9595" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </Svg>
                    <Text style={styles.statusChipText}>
                      {nextMeetingText}
                    </Text>
                  </View>
                  
                  {timeStatus.status !== 'ongoing' && (
                    <Text style={styles.badgeText}>{timeStatus.message}</Text>
                  )}
                </View>
              </View>
            </LinearGradient>
          </ImageBackground>
          
          {/* Modal Header overlay */}
          <View style={styles.modalHeader}>
            <TouchableOpacity
              onPress={() => {
                import('@/utils/clarityEvents')
                  .then(({ logUiAction }) =>
                    logUiAction({ surface: 'meeting_detail', element: 'back' })
                  )
                  .catch(() => {});
                router.back();
              }}
              style={styles.modalBackButton}
            >
              <Svg width="25" height="24" viewBox="0 0 25 24" fill="none">
                <Path d="M16.0859 19L9.08594 12L16.0859 5" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
              </Svg>
            </TouchableOpacity>
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.actionButtonsContainer}>
          {/* PRD §5 / §7 — Entrar CTA is conditional on a non-empty
              meeting_link AND the meeting being live right now. When the
              catalog row has no link, or the meeting is not currently in
              its live window, we hide the Entrar button entirely and
              leave Favoritar + Convidar as the only two actions. */}
          {!!meeting.meeting_link && !!meeting.meeting_time && isMeetingLive(meeting.meeting_time, meeting.meeting_weekday, t) && (
          <TouchableOpacity 
            style={[styles.actionButton, showPlusGate ? styles.actionButtonPlus : styles.actionButtonPrimary]}
            onPress={handleEntrar}
            disabled={isCheckinLoading}
          >
            {showPlusGate ? (
              <>
                {/* Use the same Plus gem icon as the rest of the app
                    (matches encontros-online's GemIconStroke) */}
                <Svg width="16" height="16" viewBox="0 0 12 12" fill="none">
                  <Path d="M1.75524 5.7637L5.40815 9.7771C5.72535 10.1259 6.2741 10.1259 6.59125 9.7771L10.2447 5.7632C10.5502 5.42805 10.5852 4.92698 10.3298 4.55191L8.8587 2.39099C8.6763 2.12343 8.37275 1.96289 8.04875 1.96289H3.95555C3.63156 1.96289 3.32849 2.12294 3.14606 2.3905L1.67059 4.55191C1.4147 4.92649 1.44973 5.42805 1.75524 5.7637Z" stroke="#FFFFFF" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M6.99847 1.96289L7.68197 4.55191C7.78117 4.92698 7.76757 5.42805 7.64887 5.7632L6.22887 9.7771C6.10577 10.1259 5.89222 10.1259 5.76912 9.7771L4.34912 5.7637C4.23042 5.42805 4.2168 4.92649 4.31653 4.55191L5.00197 1.96289" stroke="#FFFFFF" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M1.50391 5.10156H10.4954" stroke="#FFFFFF" strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
                <Text style={styles.actionButtonPrimaryText}>Plus</Text>
              </>
            ) : (
              <>
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path 
                d="M11.9258 7.26465C12.8558 7.13398 13.5718 6.33665 13.5738 5.37065C13.5738 4.41865 12.8798 3.62931 11.9698 3.47998" 
                stroke="white" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <Path 
                d="M13.1523 9.50049C14.053 9.63515 14.6817 9.95049 14.6817 10.6005C14.6817 11.0478 14.3857 11.3385 13.907 11.5212" 
                stroke="white" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <Path 
                fillRule="evenodd" 
                clipRule="evenodd" 
                d="M7.92579 9.77588C5.78312 9.77588 3.95312 10.1005 3.95312 11.3972C3.95312 12.6932 5.77179 13.0272 7.92579 13.0272C10.0685 13.0272 11.8978 12.7059 11.8978 11.4085C11.8978 10.1112 10.0798 9.77588 7.92579 9.77588Z" 
                stroke="white" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <Path 
                fillRule="evenodd" 
                clipRule="evenodd" 
                d="M7.92494 7.9255C9.33094 7.9255 10.4709 6.78616 10.4709 5.3795C10.4709 3.9735 9.33094 2.8335 7.92494 2.8335C6.51894 2.8335 5.37892 3.9735 5.37892 5.3795C5.37361 6.78083 6.50494 7.92083 7.90628 7.9255H7.92494Z" 
                stroke="white" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <Path 
                d="M3.92534 7.26465C2.99468 7.13398 2.27934 6.33665 2.27734 5.37065C2.27734 4.41865 2.97134 3.62931 3.88134 3.47998" 
                stroke="white" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <Path 
                d="M2.6973 9.50049C1.79664 9.63515 1.16797 9.95049 1.16797 10.6005C1.16797 11.0478 1.46397 11.3385 1.94264 11.5212" 
                stroke="white" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </Svg>
            <Text style={styles.actionButtonPrimaryText}>{t('meetings.actions.enter')}</Text>
              </>
            )}
          </TouchableOpacity>
          )}

          <TouchableOpacity 
            style={[styles.actionButton, isSaved && styles.actionButtonSaved]}
            onPress={handleFavoritar}
          >
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path 
                fillRule="evenodd" 
                clipRule="evenodd" 
                d="M8.73617 2.78483L9.95445 5.21867C10.0738 5.4576 10.3043 5.62328 10.5716 5.66162L13.2969 6.05391C13.9702 6.15112 14.2382 6.96719 13.7509 7.43479L11.7801 9.32846C11.5864 9.51467 11.4983 9.78236 11.5441 10.0453L12.0092 12.7187C12.1237 13.3801 11.4198 13.8846 10.818 13.5717L8.38214 12.3086C8.14335 12.1847 7.85735 12.1847 7.61786 12.3086L5.182 13.5717C4.58015 13.8846 3.87626 13.3801 3.9915 12.7187L4.4559 10.0453C4.50171 9.78236 4.41355 9.51467 4.21988 9.32846L2.24912 7.43479C1.76181 6.96719 2.02976 6.15112 2.70311 6.05391L5.42843 5.66162C5.69569 5.62328 5.92685 5.4576 6.04625 5.21867L7.26383 2.78483C7.5651 2.18305 8.4349 2.18305 8.73617 2.78483Z" 
                stroke={isSaved ? "#C48000" : "#151515"}
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                fill={isSaved ? "#C48000" : "none"}
              />
            </Svg>
            <Text style={[styles.actionButtonText, isSaved && styles.actionButtonTextSaved]}>{isSaved ? t('meetings.actions.favorited') : t('meetings.actions.favorite')}</Text>
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.actionButton}
            onPress={handleConvidar}
          >
            <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <Path 
                d="M8.89173 6.73389L6.59375 9.03187" 
                stroke="#151515" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
              <Path 
                fillRule="evenodd" 
                clipRule="evenodd" 
                d="M6.59544 9.03184L2.0521 6.25384C1.45677 5.88984 1.57744 4.99184 2.2481 4.79784L12.9748 1.69917C13.5848 1.5225 14.1488 2.09184 13.9661 2.69984L10.7834 13.3425C10.5841 14.0092 9.6901 14.1238 9.32877 13.5292L6.59544 9.03184Z" 
                stroke="#151515" 
                strokeWidth="1.5" 
                strokeLinecap="round" 
                strokeLinejoin="round"
              />
            </Svg>
            <Text style={styles.actionButtonText}>{t('meetings.actions.invite')}</Text>
          </TouchableOpacity>
        </View>

        {/* Avatar Group */}
        <View style={styles.avatarGroupContainer}>
          <View style={styles.avatarGroup}>
            {recentAttendees.slice(0, 3).map((attendee, index) => (
              <View key={`avatar-${attendee.userId}-${index}`} style={[styles.avatarWrapper, { marginLeft: index > 0 ? -8 : 0 }]}>
                <Avatar
                  uri={attendee.profilePhotoUrl}
                  size={32}
                  outerBorderWidth={4}
                  borderColor="#F9F8F3"
                  useCache={false}
                />
              </View>
            ))}
            {/* Show placeholder avatars if less than 3 attendees */}
            {Array.from({ length: Math.max(0, 3 - recentAttendees.length) }).map((_, index) => (
              <View key={`placeholder-${index}`} style={[styles.avatarWrapper, { marginLeft: recentAttendees.length + index > 0 ? -8 : 0 }]}>
                <Avatar
                  size={32}
                  outerBorderWidth={4}
                  borderColor="#F9F8F3"
                />
              </View>
            ))}
          </View>
          <Text style={styles.participantText}>{participantCount} {t('meetings.detail.totalParticipations')}</Text>
        </View>

        {/* Tags Section */}
        <View style={styles.tagsSection}>
          <View style={styles.tagsContainer}>
            {groupName && groupSlug && (
              <TouchableOpacity 
                style={styles.tagChip}
                onPress={() => router.replace(`/group/${encodeURIComponent(groupSlug)}`)}
              >
                <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <Path fillRule="evenodd" clipRule="evenodd" d="M4.24895 8.77344C3.00613 8.77437 1.99934 9.78257 2 11.0254C2.00067 12.2682 3.00853 13.2752 4.25135 13.2749C5.49417 13.2746 6.5015 12.267 6.5015 11.0242C6.50134 10.427 6.26391 9.8543 5.84146 9.43217C5.41901 9.01004 4.84615 8.7731 4.24895 8.77344Z" stroke="#669AB7" strokeWidth="1.5" />
                  <Path fillRule="evenodd" clipRule="evenodd" d="M11.749 8.77344C10.5061 8.77437 9.4994 9.78257 9.5 11.0254C9.50067 12.2682 10.5085 13.2752 11.7514 13.2749C12.9942 13.2746 14.0015 12.267 14.0015 11.0242C14.0014 10.427 13.7639 9.8543 13.3415 9.43217C12.9191 9.01004 12.3462 8.7731 11.749 8.77344Z" stroke="#669AB7" strokeWidth="1.5" />
                  <Path fillRule="evenodd" clipRule="evenodd" d="M7.99892 2.72656C6.75612 2.72756 5.74934 3.7357 5.75 4.97852C5.75067 6.22134 6.75852 7.2284 8.00132 7.22807C9.24419 7.22774 10.2515 6.22014 10.2515 4.97732C10.2513 4.38012 10.0139 3.80745 9.59145 3.38534C9.16899 2.96322 8.59612 2.72625 7.99892 2.72656Z" stroke="#669AB7" strokeWidth="1.5" />
                </Svg>
                <Text style={styles.tagChipText}>{groupName}</Text>
              </TouchableOpacity>
            )}
            {meeting.meeting_category && (
              <View style={styles.tagChipCategory}>
                <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <Path d="M8 1.3335V4.00016" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M8 12V14.6667" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M14.668 8H12.0013" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M4 8H1.33333" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M3.28516 3.28613L5.17077 5.17175" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M10.8281 10.8286L12.7137 12.7142" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M12.7148 3.28613L10.8292 5.17175" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                  <Path d="M5.17188 10.8286L3.28626 12.7142" stroke="#151515" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </Svg>
                <Text style={styles.tagChipCategoryText}>{translateCategory(meeting.meeting_category, t)}</Text>
              </View>
            )}
          </View>
        </View>

        {/* Description */}
        {meeting.description && (
          <View style={styles.descriptionContainer}>
            <Text style={styles.descriptionText}>
              {meeting.description}
            </Text>
          </View>
        )}

        {/* Meeting Information */}
        <View style={styles.infoContainer}>
          <Text style={styles.infoTitle}>{t('meetings.detail.infoTitle')}</Text>
          
          <View style={styles.infoCard}>
            {/* Password */}
            {meeting.meeting_password && (
              <View style={styles.infoRow}>
                <Text style={styles.infoLabel}>{t('meetings.labels.password')}</Text>
                <View style={styles.infoValueRow}>
                  <Text style={styles.infoValue}>{meeting.meeting_password}</Text>
                  <TouchableOpacity onPress={handleCopyPassword}>
                    <Ionicons name="copy-outline" size={24} color="#151515" />
                  </TouchableOpacity>
                </View>
              </View>
            )}

            {/* Place */}
            <View style={styles.infoRow}>
              <Text style={styles.infoLabel}>{t('meetings.labels.place')}</Text>
              <Text style={styles.infoValue}>{translatePlatform(meeting.meeting_place, t)}</Text>
            </View>
          </View>
        </View>

        {/* Programação Accordion (PRD §7.5) — one row per session, Mon→Sun. */}
        {sessions.length > 0 && (
          <View style={styles.tipsSection}>
            <View style={styles.tipsCard}>
              <TouchableOpacity
                style={styles.tipsHeader}
                onPress={() => {
                  const next = !programacaoExpanded;
                  import('@/utils/clarityEvents')
                    .then(({ logUiAction }) =>
                      logUiAction({
                        surface: 'meeting_detail',
                        element: 'programacao',
                        action: next ? 'expanded' : 'dismissed',
                      })
                    )
                    .catch(() => {});
                  setProgramacaoExpanded(next);
                }}
                activeOpacity={0.7}
              >
                <View style={styles.programacaoTitleBlock}>
                  <Text style={styles.tipsTitle}>{t('meetings.detail.programacao')}</Text>
                  <Text style={styles.programacaoSubtitle}>
                    {sessions.length === 1
                      ? t('meetings.detail.programacaoCountOne')
                      : t('meetings.detail.programacaoCount', { count: sessions.length })}
                  </Text>
                </View>
                <Svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ transform: [{ rotate: programacaoExpanded ? '180deg' : '0deg' }] }}>
                  <Path d="M12.8333 5.6665L8.16667 10.3332L3.5 5.6665" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
                </Svg>
              </TouchableOpacity>
              {programacaoExpanded && (
                <View style={styles.programacaoList}>
                {sessions.map((s) => {
                  const wdLabel = translateWeekdays([normalizeWeekdayValue(s.weekday) || s.weekday], t);
                  const time = formatDisplayTime(s.meeting_time);
                  const cat = s.meeting_category ? translateCategory(s.meeting_category, t) : null;
                  const isCurrent = !!(meeting as any)?.next_session?.session_id && (meeting as any).next_session.session_id === s.id;
                  const iconStroke = isCurrent ? '#FFFFFF' : '#7E7D75';
                  return (
                    <View key={s.id} style={styles.programacaoRow}>
                      <View style={[styles.programacaoIconContainer, isCurrent && styles.programacaoIconContainerCurrent]}>
                        <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                          <Path fillRule="evenodd" clipRule="evenodd" d="M10.4566 3.37598L4.94039 3.37652C3.32005 3.37652 2.30859 4.27884 2.30859 5.93735V10.9302C2.30859 12.6149 3.32005 13.5381 4.94039 13.5381L10.4519 13.5376C12.077 13.5376 13.0837 12.6305 13.0837 10.9714V5.9368C13.0885 4.27829 12.0818 3.37598 10.4566 3.37598Z" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <Path d="M5.01562 7.625H5.02229" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <Path d="M5.01562 9.92773H5.02229" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <Path d="M7.69141 7.625H7.69807" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <Path d="M7.69141 9.92773H7.69807" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <Path d="M10.3594 7.625H10.366" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <Path d="M10.3594 9.92773H10.366" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <Path d="M10.1016 2.46143V4.44497" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                          <Path d="M5.28516 2.46143V4.44497" stroke={iconStroke} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </Svg>
                      </View>
                      <Text style={styles.programacaoWeekday} numberOfLines={1}>
                        {wdLabel}{time ? ` · ${time}` : ''}
                      </Text>
                      {cat ? (
                        <Text style={styles.programacaoCategory} numberOfLines={1}>{cat}</Text>
                      ) : null}
                    </View>
                  );
                })}
                </View>
              )}
            </View>
          </View>
        )}

        {/* Meeting Tips Accordion */}
        <View style={styles.tipsSection}>
          <View style={styles.tipsCard}>
            <TouchableOpacity
              style={styles.tipsHeader}
              onPress={() => {
                const next = !tipsExpanded;
                import('@/utils/clarityEvents')
                  .then(({ logUiAction }) =>
                    logUiAction({
                      surface: 'meeting_detail',
                      element: 'tips',
                      action: next ? 'expanded' : 'dismissed',
                    })
                  )
                  .catch(() => {});
                setTipsExpanded(next);
              }}
              activeOpacity={0.7}
            >
              <Text style={styles.tipsTitle}>{t('meetings.detail.tipsTitle')}</Text>
              <Svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ transform: [{ rotate: tipsExpanded ? '180deg' : '0deg' }] }}>
                <Path d="M12.8333 5.6665L8.16667 10.3332L3.5 5.6665" stroke="#151515" strokeWidth="2" strokeLinecap="square" />
              </Svg>
            </TouchableOpacity>
            {tipsExpanded && (
              <View style={styles.tipsList}>
                <View style={styles.tipRow}>
                  <View style={styles.tipIconContainer}>
                    <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <Path fillRule="evenodd" clipRule="evenodd" d="M10.193 2.201C10.8663 2.469 11.0723 3.40233 11.3477 3.70233C11.623 4.00233 12.017 4.10433 12.235 4.10433C13.3937 4.10433 14.333 5.04367 14.333 6.20167V10.065C14.333 11.6183 13.073 12.8783 11.5197 12.8783H4.81303C3.25903 12.8783 1.99969 11.6183 1.99969 10.065V6.20167C1.99969 5.04367 2.93903 4.10433 4.09769 4.10433C4.31503 4.10433 4.70903 4.00233 4.98503 3.70233C5.26036 3.40233 5.46569 2.469 6.13903 2.201C6.81303 1.933 9.51969 1.933 10.193 2.201Z" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <Path d="M11.832 5.8335H11.838" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <Path fillRule="evenodd" clipRule="evenodd" d="M10.2855 8.25215C10.2855 7.08148 9.33688 6.13281 8.16621 6.13281C6.99554 6.13281 6.04688 7.08148 6.04688 8.25215C6.04688 9.42281 6.99554 10.3715 8.16621 10.3715C9.33688 10.3715 10.2855 9.42281 10.2855 8.25215Z" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </Svg>
                  </View>
                  <Text style={styles.tipText}>{t('meetings.detail.tipCamera')}</Text>
                </View>
                <View style={styles.tipRow}>
                  <View style={styles.tipIconContainer}>
                    <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <Path d="M4.8614 11.3864C4.79678 11.3721 4.73893 11.3721 4.68182 11.3721C3.57504 11.2925 3.21512 11.4225 2.60425 10.919C1.95731 10.3728 2.0009 8.9211 2.0009 7.85717C2.0009 6.79997 1.95055 5.3483 2.60425 4.80205C3.20836 4.29862 3.57504 4.42861 4.68182 4.34896C4.97636 4.34896 5.32125 4.2633 5.57296 4.10476L7.73693 2.75378C9.0376 1.94154 10.626 2.91157 10.7199 4.42861C10.7417 4.7735 10.7627 5.12514 10.7771 5.47078" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <Path d="M6.98047 12.5L7.7356 12.9674C9.0362 13.7796 10.6397 12.8021 10.7185 11.2926C10.7757 10.4082 10.8193 9.53137 10.8335 8.64697" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <Path d="M2.5 13.75L14.0014 2.24854" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </Svg>
                  </View>
                  <Text style={styles.tipText}>{t('meetings.detail.tipMute')}</Text>
                </View>
                <View style={styles.tipRow}>
                  <View style={styles.tipIconContainer}>
                    <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <Path d="M8.00113 3.95021C7.5986 3.49952 7.08167 3.15168 6.51099 2.95138C4.31013 2.17122 2.25365 3.73139 2.02105 5.91834C1.6954 9.21403 5.22341 11.2409 7.4244 13.0482C7.67213 13.2548 8.03413 13.3115 8.3326 13.1878C8.47587 13.1335 8.59107 13.0303 8.70773 12.9344C10.9276 11.1152 14.4348 9.02403 13.9556 5.70462C13.6007 3.38595 11.1802 2.08414 9.0926 3.11858C8.6802 3.32289 8.30707 3.60638 8.00113 3.95021Z" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </Svg>
                  </View>
                  <Text style={styles.tipText}>{t('meetings.detail.tipHeart')}</Text>
                </View>
                <View style={styles.tipRow}>
                  <View style={styles.tipIconContainer}>
                    <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <Path d="M8.00113 3.95021C7.5986 3.49952 7.08167 3.15168 6.51099 2.95138C4.31013 2.17122 2.25365 3.73139 2.02105 5.91834C1.6954 9.21403 5.22341 11.2409 7.4244 13.0482C7.67213 13.2548 8.03413 13.3115 8.3326 13.1878C8.47587 13.1335 8.59107 13.0303 8.70773 12.9344C10.9276 11.1152 14.4348 9.02403 13.9556 5.70462C13.6007 3.38595 11.1802 2.08414 9.0926 3.11858C8.6802 3.32289 8.30707 3.60638 8.00113 3.95021Z" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </Svg>
                  </View>
                  <Text style={styles.tipText}>{t('meetings.detail.tipHonesty')}</Text>
                </View>
                <View style={styles.tipRow}>
                  <View style={styles.tipIconContainer}>
                    <Svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                      <Path d="M8.24318 8.51761C9.97394 8.51761 11.377 7.11456 11.377 5.38381C11.377 3.65305 9.97394 2.25 8.24318 2.25C6.51243 2.25 5.10938 3.65305 5.10938 5.38381C5.10938 7.11456 6.51243 8.51761 8.24318 8.51761Z" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <Path d="M3.96759 12.9318C3.89369 12.5063 3.92421 12.0654 3.92421 11.6357C3.92421 10.0161 5.23715 8.70312 6.85673 8.70312H9.71233C10.5005 8.70312 11.2161 9.01406 11.743 9.51999" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <Path d="M13.2832 10.5791L14.7482 12.0441" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                      <Path d="M14.7482 10.5791L13.2832 12.0441" stroke="#7E7D75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </Svg>
                  </View>
                  <Text style={styles.tipText}>{t('meetings.detail.tipLeave')}</Text>
                </View>
              </View>
            )}
          </View>
        </View>

        {/* Admin Edit Link */}
        {userProfile?.is_admin && (
          <View style={styles.adminEditContainer}>
            <TouchableOpacity onPress={() => router.push({ pathname: '/add-meeting', params: { editMeetingId: meeting.id } })}>
              <Text style={styles.adminEditLink}>Editar encontro</Text>
            </TouchableOpacity>
          </View>
        )}

        <View style={{ height: 40 }} />
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  modalHeader: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    paddingHorizontal: 16,
    paddingTop: 16,
    zIndex: 10,
  },
  modalBackButton: {
    width: 44,
    height: 44,
    justifyContent: 'center',
    alignItems: 'center',
  },
  headerTitleContainer: {
    flex: 1,
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 16,
    fontFamily: 'Inter_700Bold',
    color: '#151515',
    lineHeight: 19.2,
  },
  headerSpacer: {
    width: 44,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 16,
    color: '#7E7D75',
    fontFamily: 'Inter_500Medium',
  },
  scrollView: {
    flex: 1,
  },
  headerContainer: {
    width: '100%',
    height: 300,
    backgroundColor: '#FFFFFF',
    borderRadius: 0,
  },
  headerImage: {
    width: '100%',
    height: '100%',
  },
  headerImageStyle: {
    resizeMode: 'cover',
  },
  gradient: {
    flex: 1,
    justifyContent: 'flex-end',
    paddingHorizontal: 24,
    paddingTop: 24,
    paddingBottom: 0,
  },
  headerContent: {
    gap: 12,
    alignItems: 'flex-start',
  },
  plusBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingTop: 4,
    paddingRight: 8,
    paddingBottom: 4,
    paddingLeft: 4,
    backgroundColor: '#669AB7',
    borderRadius: 14,
    height: 24,
  },
  plusBadgeText: {
    fontSize: 10,
    fontFamily: 'Inter_500Medium',
    lineHeight: 14,
    color: '#FFFFFF',
  },
  meetingName: {
    fontSize: 24,
    fontFamily: 'Sentient',
    fontWeight: '400',
    lineHeight: 28.8,
    color: '#151515',
  },
  statusRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  statusChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  statusChipText: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    lineHeight: 12,
    color: '#151515',
  },
  badgeText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    lineHeight: 12,
    color: '#7E7D75',
  },
  actionButtonsContainer: {
    flexDirection: 'row',
    paddingHorizontal: 24,
    paddingTop: 24,
    gap: 8,
  },
  actionButton: {
    flex: 1,
    paddingHorizontal: 12,
    paddingVertical: 12,
    borderRadius: 12,
    backgroundColor: '#F0EEE4',
    height: 70,
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  actionButtonPrimary: {
    backgroundColor: '#151515',
  },
  actionButtonPlus: {
    backgroundColor: '#669AB7',
  },
  actionButtonSaved: {
    backgroundColor: '#F0EEE4',
  },
  actionButtonText: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    lineHeight: 12,
    color: '#151515',
  },
  actionButtonTextSaved: {
    color: '#C48000',
  },
  actionButtonPrimaryText: {
    fontSize: 12,
    fontFamily: 'Inter_700Bold',
    lineHeight: 12,
    color: '#FFFFFF',
  },
  avatarGroupContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingTop: 24,
    gap: 12,
  },
  avatarGroup: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  avatarWrapper: {
    // No additional styling needed - Avatar component handles borders
  },
  participantText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    lineHeight: 12,
    color: '#7E7D75',
  },
  tagsSection: {
    paddingHorizontal: 24,
    paddingTop: 24,
    gap: 12,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tagChip: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 8,
    backgroundColor: '#E6EBF0',
    borderRadius: 17,
  },
  tagChipText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    lineHeight: 16.8,
    color: '#669AB7',
  },
  tagChipCategory: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 8,
    backgroundColor: '#F0EEE4',
    borderRadius: 17,
  },
  tagChipCategoryText: {
    fontSize: 12,
    fontFamily: 'Inter_500Medium',
    lineHeight: 16.8,
    color: '#151515',
  },
  descriptionContainer: {
    paddingHorizontal: 24,
    paddingTop: 24,
  },
  descriptionText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    lineHeight: 19.6,
    color: '#151515',
  },
  infoContainer: {
    paddingHorizontal: 24,
    paddingTop: 12,
  },
  infoTitle: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    lineHeight: 14,
    color: '#7E7D75',
    marginBottom: 12,
  },
  infoCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    padding: 20,
    gap: 16,
  },
  infoRow: {
    gap: 4,
  },
  infoLabel: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    lineHeight: 19.6,
    color: '#151515',
  },
  infoValue: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    lineHeight: 19.6,
    color: '#151515',
    flex: 1,
  },
  infoValueRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  adminEditContainer: {
    paddingHorizontal: 24,
    paddingTop: 24,
    alignItems: 'center',
  },
  adminEditLink: {
    fontSize: 14,
    fontFamily: 'Inter_500Medium',
    lineHeight: 14,
    color: '#015480',
    textDecorationLine: 'underline',
  },
  tipsSection: {
    paddingHorizontal: 24,
    paddingTop: 12,
  },
  tipsCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#F0EEE4',
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 24,
  },
  tipsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  tipsTitle: {
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    lineHeight: 19.6,
    color: '#151515',
    flex: 1,
  },
  programacaoTitleBlock: {
    flex: 1,
    gap: 2,
  },
  programacaoSubtitle: {
    fontSize: 12,
    fontFamily: 'Inter_400Regular',
    lineHeight: 16.8,
    color: '#7E7D75',
  },
  tipsList: {
    gap: 24,
  },
  programacaoList: {
    gap: 24,
  },
  programacaoRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    height: 32,
  },
  programacaoIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 20,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  programacaoIconContainerCurrent: {
    backgroundColor: '#669AB7',
  },
  programacaoWeekday: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Inter_700Bold',
    lineHeight: 19.6,
    color: '#151515',
  },
  programacaoCategory: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    lineHeight: 19.6,
    color: '#151515',
  },
  tipRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 24,
  },
  tipIconContainer: {
    width: 32,
    height: 32,
    borderRadius: 20,
    backgroundColor: '#F0EEE4',
    justifyContent: 'center',
    alignItems: 'center',
  },
  tipText: {
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    lineHeight: 19.6,
    color: '#151515',
    flex: 1,
  },
});
